import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { PRIORITIES, TASK_STATUSES } from "@/lib/constants";
import { useAssignableOptions } from "@/hooks/useData";
import { Field, NSelect } from "@/components/common";
import DatePicker from "@/components/DatePicker";

const BLANK = { title: "", description: "", assigned_to: "", due_date: "", priority: "Medium", status: "To Do", lead_id: "", property_id: "" };

export default function TaskDialog({ open, onOpenChange, task, defaults, onSaved }) {
  const qc = useQueryClient();
  const assignOpts = useAssignableOptions();
  const [f, setF] = useState(BLANK);
  const [busy, setBusy] = useState(false);
  const { data: leads = [] } = useQuery({ queryKey: ["leads", {}], queryFn: async () => (await api.get("/leads")).data, enabled: open });
  const { data: props = [] } = useQuery({ queryKey: ["properties", {}], queryFn: async () => (await api.get("/properties")).data, enabled: open });
  const init = () => setF(task ? { ...BLANK, ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, task[k] ?? ""])) } : { ...BLANK, ...defaults });
  const s = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.assigned_to) return toast.error("Choose who the task is assigned to");
    setBusy(true);
    const body = { ...f, due_date: f.due_date || null, lead_id: f.lead_id || null, property_id: f.property_id || null };
    try {
      const { data } = task
        ? await api.patch(`/tasks/${task.id}`, body)
        : await api.post("/tasks", { ...body, source: defaults?.source_message_id ? "Chat" : "Manual", source_message_id: defaults?.source_message_id || null });
      toast.success(task ? "Task updated" : "Task created");
      qc.invalidateQueries({ queryKey: ["tasks"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      onSaved?.(data);
      onOpenChange(false);
    } catch (ex) {
      toast.error(formatErr(ex));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={init} className="max-h-[90vh] overflow-y-auto" data-testid="task-dialog">
        <DialogHeader><DialogTitle>{task ? "Edit task" : "New task"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Title" required><Input data-testid="task-title-input" value={f.title} onChange={(e) => s("title")(e.target.value)} required /></Field>
          <Field label="Description"><Textarea data-testid="task-description-input" value={f.description} onChange={(e) => s("description")(e.target.value)} rows={3} /></Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Assigned to" required><NSelect testid="task-assigned-select" value={f.assigned_to} onChange={s("assigned_to")} placeholder="Choose…" options={assignOpts} /></Field>
            <Field label="Due date"><DatePicker testid="task-due-input" value={f.due_date} onChange={s("due_date")} /></Field>
            <Field label="Priority"><NSelect testid="task-priority-select" value={f.priority} onChange={s("priority")} options={PRIORITIES} /></Field>
            <Field label="Status"><NSelect testid="task-status-select" value={f.status} onChange={s("status")} options={TASK_STATUSES} /></Field>
            <Field label="Linked lead"><NSelect testid="task-lead-select" value={f.lead_id} onChange={s("lead_id")} placeholder="None" options={leads.map((l) => ({ value: l.id, label: `${l.name} (${l.phone})` }))} /></Field>
            <Field label="Linked property"><NSelect testid="task-property-select" value={f.property_id} onChange={s("property_id")} placeholder="None" options={props.map((p) => ({ value: p.id, label: `${p.property_type} – ${p.project || p.sector}` }))} /></Field>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="task-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{task ? "Save" : "Create task"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
